var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { NF_CELL_SIZE } from '../constants/gridConstants';
var CELL_SIZE = NF_CELL_SIZE;
var GRID_PADDING = 0; // No exterior padding around the grid
var GRID_BORDER = 0; // frame div is explicitly sized to include the 2px border, so content starts at halfWidth exactly
/**
 * Calculate token position from frame and cellIndex with rotation and scale support.
 * The frame uses CSS transform: scale(), so the visual offset from center is multiplied by scale.
 */
export var calculateTokenPosition = function (frame, cellIndex) {
    var _a;
    var row = Math.floor(cellIndex / frame.cols);
    var col = cellIndex % frame.cols;
    var frameScale = (_a = frame.scale) !== null && _a !== void 0 ? _a : 1;
    // Cell center offset from frame center (unscaled)
    var halfWidth = (frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2;
    var halfHeight = (frame.rows * CELL_SIZE + GRID_PADDING * 2) / 2;
    var offsetX = -halfWidth + GRID_PADDING + GRID_BORDER + col * CELL_SIZE + CELL_SIZE / 2;
    var offsetY = -halfHeight + GRID_PADDING + GRID_BORDER + row * CELL_SIZE + CELL_SIZE / 2;
    // Apply scale to offset (CSS scale expands from center)
    var dx = offsetX * frameScale;
    var dy = offsetY * frameScale;
    // Apply rotation if needed
    if (frame.rotation) {
        var angle = (frame.rotation * Math.PI) / 180;
        var rotatedDx = dx * Math.cos(angle) - dy * Math.sin(angle);
        var rotatedDy = dx * Math.sin(angle) + dy * Math.cos(angle);
        dx = rotatedDx;
        dy = rotatedDy;
    }
    return { x: frame.center.x + dx, y: frame.center.y + dy };
};
/**
 * Find empty cells in a frame using tokens map
 */
export var findEmptyCells = function (frame) {
    var totalCells = frame.rows * frame.cols;
    var occupied = new Set(Object.keys(frame.tokens || {}).map(function (k) { return parseInt(k); }));
    var empty = [];
    for (var i = 0; i < totalCells; i++) {
        if (!occupied.has(i)) {
            empty.push(i);
        }
    }
    return empty;
};
/**
 * Find which numberFrame contains a position (with zoom and scroll)
 * @param screenX - X coordinate relative to the scroll container (not viewport)
 * @param screenY - Y coordinate relative to the scroll container (not viewport)
 * @param manipulative - Array of all manipulatives
 * @param zoomLevel - Current zoom level
 * @param scrollOffset - Scroll offset {x: scrollLeft, y: scrollTop}
 */
export var findNumberFrameAtPosition = function (screenX, screenY, manipulative, zoomLevel, scrollOffset) {
    var _a;
    if (scrollOffset === void 0) { scrollOffset = { x: 0, y: 0 }; }
    // Convert container-relative coordinates to canvas coordinates (accounting for scroll and zoom)
    var canvasX = (screenX + scrollOffset.x) / zoomLevel;
    var canvasY = (screenY + scrollOffset.y) / zoomLevel;
    // Find frames at this position (reverse order to get top frame first)
    for (var i = manipulative.length - 1; i >= 0; i--) {
        var item = manipulative[i];
        if (item.data.type === 'numberFrames') {
            var frame = item.data;
            var frameScale = (_a = frame.scale) !== null && _a !== void 0 ? _a : 1;
            var halfWidth = ((frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2) * frameScale;
            var halfHeight = ((frame.rows * CELL_SIZE + GRID_PADDING * 2) / 2) * frameScale;
            // Un-rotate the test point around frame center to work in frame-local space
            var localX = canvasX - frame.center.x;
            var localY = canvasY - frame.center.y;
            if (frame.rotation) {
                var angle = -(frame.rotation * Math.PI) / 180;
                var rx = localX * Math.cos(angle) - localY * Math.sin(angle);
                var ry = localX * Math.sin(angle) + localY * Math.cos(angle);
                localX = rx;
                localY = ry;
            }
            var isInside = Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight;
            if (isInside) {
                return frame;
            }
        }
    }
    return null;
};
/**
 * Find nearest cell index to a position within a frame (accounts for frame scale)
 */
export var findNearestCell = function (canvasX, canvasY, frame) {
    var _a;
    var frameScale = (_a = frame.scale) !== null && _a !== void 0 ? _a : 1;
    // Get offset from frame center
    var dx = canvasX - frame.center.x;
    var dy = canvasY - frame.center.y;
    // Un-rotate around frame center (CSS applies scale then rotate,
    // so to undo: un-rotate first, then un-scale)
    if (frame.rotation) {
        var angle = -(frame.rotation * Math.PI) / 180;
        var rx = dx * Math.cos(angle) - dy * Math.sin(angle);
        var ry = dx * Math.sin(angle) + dy * Math.cos(angle);
        dx = rx;
        dy = ry;
    }
    // Un-scale and convert to frame-local coordinates
    var halfWidth = (frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2;
    var halfHeight = (frame.rows * CELL_SIZE + GRID_PADDING * 2) / 2;
    var relativeX = dx / frameScale + halfWidth - GRID_PADDING - GRID_BORDER;
    var relativeY = dy / frameScale + halfHeight - GRID_PADDING - GRID_BORDER;
    var col = Math.floor(relativeX / CELL_SIZE);
    var row = Math.floor(relativeY / CELL_SIZE);
    // Clamp to frame bounds
    var clampedCol = Math.max(0, Math.min(frame.cols - 1, col));
    var clampedRow = Math.max(0, Math.min(frame.rows - 1, row));
    return clampedRow * frame.cols + clampedCol;
};
/**
 * Create counters with frame placement (updates frame's tokens map)
 */
export var placeTokensInFrame = function (frame, variant, color, shape, startCellIndex) {
    var _a;
    var emptyCells = findEmptyCells(frame);
    var tokensToPlace = variant;
    var totalCells = frame.rows * frame.cols;
    var frameScale = (_a = frame.scale) !== null && _a !== void 0 ? _a : 1;
    // Build a visual fill order so tokens always fill left-to-right as seen in the
    // rotated frame, regardless of the underlying cell-index layout.
    // Sort all cell indices by their computed canvas position: primary by visual Y
    // (rows in the rotated frame), secondary by visual X (columns).
    var cellVisualPositions = Array.from({ length: totalCells }, function (_, i) { return ({
        index: i,
        pos: calculateTokenPosition(frame, i),
    }); });
    var rowTolerance = CELL_SIZE * frameScale * 0.5;
    cellVisualPositions.sort(function (a, b) {
        var yDiff = a.pos.y - b.pos.y;
        if (Math.abs(yDiff) > rowTolerance)
            return yDiff;
        return a.pos.x - b.pos.x;
    });
    var visualOrder = cellVisualPositions.map(function (cp) { return cp.index; });
    // Find the position of the drop cell in the visual order and fill forward from there
    var startVisualIdx = visualOrder.indexOf(startCellIndex);
    // Find empty cells starting from the drop cell in visual order, wrapping around
    var availableCells = [];
    for (var i = 0; i < totalCells && availableCells.length < tokensToPlace; i++) {
        var cellIndex = visualOrder[(startVisualIdx + i) % totalCells];
        if (emptyCells.includes(cellIndex)) {
            availableCells.push(cellIndex);
        }
    }
    var baseTokenSize = shape === 'shape2' ? 55 : 50;
    var tokenW = baseTokenSize * frameScale;
    var tokenH = baseTokenSize * frameScale;
    var newCounters = [];
    var overflowCounters = [];
    var updatedTokensMap = __assign({}, (frame.tokens || {}));
    // Place as many as fit in frame
    availableCells.forEach(function (cellIndex, idx) {
        var _a;
        var tokenId = "numberFrameCounter_".concat(Date.now(), "_").concat(Math.random().toString(36).substr(2, 9));
        var position = calculateTokenPosition(frame, cellIndex);
        newCounters.push({
            type: 'numberFrameCounter',
            data: {
                id: tokenId,
                type: 'numberFrameCounter',
                shape: shape,
                color: color,
                center: position,
                dimensions: { width: tokenW, height: tokenH },
                scale: frameScale,
                rotation: (_a = frame.rotation) !== null && _a !== void 0 ? _a : 0,
                lock: { positioning: false, editing: false },
                frameId: frame.id,
                defaultDimensions: { width: baseTokenSize, height: baseTokenSize },
            },
        });
        // Update frame's token map
        updatedTokensMap[cellIndex] = tokenId;
    });
    // Place overflow tokens outside frame (account for scale in frame bounds)
    var overflowCount = tokensToPlace - availableCells.length;
    if (overflowCount > 0) {
        var frameRight = frame.center.x + ((frame.cols * CELL_SIZE + GRID_PADDING * 2) / 2) * frameScale;
        for (var i = 0; i < overflowCount; i++) {
            var tokenId = "numberFrameCounter_".concat(Date.now(), "_").concat(Math.random().toString(36).substr(2, 9));
            overflowCounters.push({
                type: 'numberFrameCounter',
                data: {
                    id: tokenId,
                    type: 'numberFrameCounter',
                    shape: shape,
                    color: color,
                    center: {
                        x: frameRight + 30 + i * CELL_SIZE,
                        y: frame.center.y,
                    },
                    dimensions: { width: baseTokenSize, height: baseTokenSize },
                    scale: 1,
                    rotation: 0,
                    lock: { positioning: false, editing: false },
                    defaultDimensions: { width: baseTokenSize, height: baseTokenSize },
                    // No frameId for overflow tokens
                },
            });
        }
    }
    return { newCounters: newCounters, overflowCounters: overflowCounters, updatedTokensMap: updatedTokensMap };
};
/**
 * Create counters at canvas position (not in frame)
 */
export var createCountersAtPosition = function (canvasX, canvasY, variant, color, shape) {
    var counters = [];
    var baseTokenSize = shape === 'shape2' ? 55 : 50;
    for (var i = 0; i < variant; i++) {
        var tokenId = "numberFrameCounter_".concat(Date.now(), "_").concat(Math.random().toString(36).substr(2, 9));
        counters.push({
            type: 'numberFrameCounter',
            data: {
                id: tokenId,
                type: 'numberFrameCounter',
                shape: shape,
                color: color,
                center: {
                    x: canvasX + i * CELL_SIZE, // Spread horizontally
                    y: canvasY,
                },
                dimensions: { width: baseTokenSize, height: baseTokenSize },
                scale: 1,
                rotation: 0,
                lock: { positioning: false, editing: false },
                defaultDimensions: { width: baseTokenSize, height: baseTokenSize },
            },
        });
    }
    return counters;
};
/**
 * Screen to canvas coordinate transformation
 */
export var screenToCanvas = function (screenX, screenY, zoomLevel, panOffset) {
    return {
        x: (screenX - panOffset.x) / zoomLevel,
        y: (screenY - panOffset.y) / zoomLevel,
    };
};
